import { useState } from 'react'
import type { OverrideStatus } from '../../../lib/verification/types'

interface OverrideDialogProps {
  checkTitle: string
  pendingStatus: OverrideStatus
  initialNotes?: string
  saving: boolean
  error: string | null
  onCancel: () => void
  onSave: (notes: string) => void
}

export default function OverrideDialog({ checkTitle, pendingStatus, initialNotes, saving, error, onCancel, onSave }: OverrideDialogProps) {
  const [notes, setNotes] = useState(initialNotes ?? '')

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50"
      onClick={e => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full p-6 shadow-xl">
        <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
          {pendingStatus === 'accepted' ? 'Accept anyway' : 'Reject'} — {checkTitle}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
          A reason is required and will be recorded on the verification report.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-400 text-sm">
            {error}
          </div>
        )}

        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Reason / notes</label>
        <textarea
          value={notes}
          onChange={e => setNotes(e.target.value)}
          rows={4}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm placeholder-gray-400 dark:placeholder-gray-500 resize-none focus:ring-2 focus:ring-blue-600 focus:border-transparent"
          placeholder="Why is this being overridden?"
          autoFocus
        />

        <div className="flex gap-3 mt-5">
          <button
            onClick={onCancel}
            disabled={saving}
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => onSave(notes.trim())}
            disabled={saving || notes.trim().length === 0}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white font-semibold rounded-lg transition-colors"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  )
}
